import React from 'react';
import Table from '../../components/ui/Table.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { useApi } from '../../hooks/useApi.js';
import { apiFetch } from '../../api.js';
import { Link } from '../../router.jsx';

export default function StaffSurveys() {
  const { data, loading, error } = useApi('/api/surveys');
  const items = data?.items || [];
  const [responses, setResponses] = React.useState({});

  const idKey = React.useMemo(() => items.map((item) => item.id).join(','), [items]);

  React.useEffect(() => {
    let active = true;
    if (!items.length) {
      setResponses({});
      return () => {
        active = false;
      };
    }

    setResponses((prev) => {
      const next = {};
      items.forEach((item) => {
        next[item.id] = prev[item.id] || { total: null, loading: true, error: '' };
      });
      return next;
    });

    Promise.all(items.map(async (survey) => {
      try {
        const result = await apiFetch(`/api/surveys/${survey.id}/results`);
        return { id: survey.id, total: result?.total_responses ?? 0, error: '' };
      } catch (err) {
        return { id: survey.id, total: null, error: err?.message || 'Failed to load responses' };
      }
    })).then((results) => {
      if (!active) return;
      setResponses((prev) => {
        const next = { ...prev };
        results.forEach((res) => {
          next[res.id] = { total: res.total, loading: false, error: res.error };
        });
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [idKey]);

  const columns = [
    { key: 'name', label: 'Survey' },
    { key: 'term', label: 'Term' },
    { key: 'status', label: 'Status' },
    { key: 'responses', label: 'Responses' },
    { key: 'actions', label: 'Actions' }
  ];

  const tableData = items.map((survey) => {
    const term = [survey.semester, survey.year].filter(Boolean).join(' ').trim() || '—';
    const title = survey.name || term || `Survey ${survey.id}`;
    const responseState = responses[survey.id];
    const responseLabel = responseState?.loading
      ? 'Loading...'
      : responseState?.error
        ? '—'
        : String(responseState?.total ?? 0);
    return {
      name: <Link to={`/staff/surveys/${survey.id}`}>{title}</Link>,
      term,
      status: survey.status === 1 ? <Badge tone="success">Active</Badge> : <Badge tone="info">Closed</Badge>,
      responses: responseLabel,
      actions: <Link to={`/staff/surveys/${survey.id}`} className="btn btn-ghost">View</Link>
    };
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Surveys</h1>
          <p className="subtle">Create and manage course demand surveys.</p>
        </div>
        <Link to="/staff/surveys/new" className="btn btn-primary">New Survey</Link>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {loading ? <p className="subtle">Loading...</p> : <Table columns={columns} data={tableData} />}
    </div>
  );
}
